import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import styles from './UserProfilePage.module.css';
import layoutStyles from '../styles/layout.module.css';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserProfile,
  updateUserProfile,
  UserProfile,
  UpdateUserProfilePayload,
  ApiErrorResponse
} from '../services/authService';
import Alert from '../components/common/Alert';

const UserProfilePage: React.FC = () => {
  const { currentUser, login } = useAuth();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [gender, setGender] = useState('');
  const [dateBirth, setDateBirth] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) {
        setError('You must be logged in to view your profile.');
        setIsLoading(false);
        return;
      }
      try {
        setError(null);
        setIsLoading(true);
        const userProfileData = await getUserProfile();
        setProfile(userProfileData);
        setFirstName((userProfileData as any).first_name || (userProfileData as any).firstName || '');
        setLastName((userProfileData as any).last_name || (userProfileData as any).lastName || '');
        setEmail(userProfileData.email || '');
        setUsername((userProfileData as any).username || '');
        setMobileNumber((userProfileData as any).mobile_number || '');
        setGender((userProfileData as any).gender || '');
        setDateBirth((userProfileData as any).dateBirth || (userProfileData as any).date_birth ? new Date((userProfileData as any).dateBirth || (userProfileData as any).date_birth).toISOString().slice(0, 10) : '');
        setImageUrl((userProfileData as any).image_url || '');
        setProfileImagePreview((userProfileData as any).image_url || null);
        setProfileImageFile(null);
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        setError(apiError.message || 'Failed to load profile.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [currentUser]);

  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isVisible]);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type, isVisible: true });
  };

  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsUpdating(true);

    if (!profile) {
      setError('Profile data not loaded.');
      setIsUpdating(false);
      return;
    }

    const payload: UpdateUserProfilePayload & { profileImage?: File; dateBirth?: string } = {
      firstName,
      lastName,
      dateBirth,
      profileImage: profileImageFile || undefined,
    };

    try {
      const response = await updateUserProfile(payload);
      setProfile(response.user);
      setFirstName((response.user as any).first_name || (response.user as any).firstName || '');
      setLastName((response.user as any).last_name || (response.user as any).lastName || '');
      setDateBirth((response.user as any).dateBirth || (response.user as any).date_birth ? new Date((response.user as any).dateBirth || (response.user as any).date_birth).toISOString().slice(0, 10) : '');
      setImageUrl((response.user as any).image_url || '');
      setProfileImagePreview((response.user as any).image_url || null);
      setProfileImageFile(null);
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          first_name: (response.user as any).first_name || (response.user as any).firstName || '',
          last_name: (response.user as any).last_name || (response.user as any).lastName || '',
          image_url: (response.user as any).image_url || '',
        };
        await login(localStorage.getItem('authToken') || '', updatedUser);
      }
      showAlert(response.message || 'User updated successfully', 'success');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || 'Failed to update profile.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading profile...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <h2 className={styles.title}>User Profile</h2>
          <Alert 
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />
          {profile ? (
            <form onSubmit={handleSubmit} className={styles.profileForm}>
              <div className={styles.avatarContainer}>
                <div className={styles.imagePicker}>
                  <img
                    src={profileImagePreview || imageUrl || '/default-avatar.png'}
                    alt="User Avatar"
                    className={styles.avatar}
                  />
                  <label htmlFor="profileImage" className={styles.imagePickerLabel}>
                    Change Image
                    <input
                      id="profileImage"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className={styles.imagePickerInput}
                      disabled={isUpdating}
                    />
                  </label>
                </div>
                <div className={styles.avatarName}>{firstName} {lastName}</div>
                <div className={styles.avatarUsername}>@{username}</div>
              </div>
              <div className={styles.profileGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" value={email} disabled readOnly />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="firstName">First Name</label>
                  <input 
                    type="text" 
                    id="firstName" 
                    value={firstName} 
                    onChange={(e) => setFirstName(e.target.value)} 
                    required 
                    disabled={isUpdating}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="lastName">Last Name</label>
                  <input 
                    type="text" 
                    id="lastName" 
                    value={lastName} 
                    onChange={(e) => setLastName(e.target.value)} 
                    required 
                    disabled={isUpdating}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="username">Username</label>
                  <input type="text" id="username" value={username} disabled readOnly />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="mobileNumber">Mobile Number</label>
                  <input type="text" id="mobileNumber" value={mobileNumber} disabled readOnly />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="gender">Gender</label>
                  <input type="text" id="gender" value={gender} disabled readOnly />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="dateBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateBirth"
                    value={dateBirth}
                    onChange={(e) => setDateBirth(e.target.value)}
                    className={styles.input}
                    disabled={isUpdating}
                  />
                </div>
              </div>
              <button type="submit" className={styles.submitButton} disabled={isUpdating}>
                {isUpdating ? 'Updating...' : 'Update Profile'}
              </button>
            </form>
          ) : (
            !error && <p>No profile data found.</p>
          )}
          {error && !profile && <p className={styles.errorMessage}>{error}</p>}
        </main>
      </div>
    </div>
  );
};

export default UserProfilePage; 